import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { ICON_SIZE, scaledFontSize } from '@/shared/styles/typography'

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

// 日付 pill / nav row 用. legacy formatDateTimeToDate と同等の挙動.
const formatDateLabel = (d: Date): string => {
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (isSameDay(d, today)) return '今日'
  if (isSameDay(d, yesterday)) return '昨日'
  return `${d.getMonth() + 1}月${d.getDate()}日`
}

interface Props {
  date: Date
  onChange: (date: Date) => void
}

// Sidebar の日付の帯 [<] 日付 [>] (design-book 02-components/sidebar.md「日付の帯」).
// 薬剤師の患者リストと管理栄養士の記録の一覧が同じものを使う.
// label を sidebar bg に同化させるため FILTER_ROW_SX (= border/bg) は被せない. height/mb のみ揃える.
// label click で今日へ (!isToday の時). forward は今日以降 disable. back は無制限 (空なら空表示).
export const SidebarDateStrip = ({ date, onChange }: Props) => {
  const isToday = isSameDay(date, new Date())

  const shift = (days: number) => {
    const d = new Date(date)
    d.setDate(d.getDate() + days)
    onChange(d)
  }
  const jumpToToday = () => onChange(new Date())

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        height: 28,
        width: '100%',
        mb: 0.75,
        px: 0.25,
      }}
    >
      <IconButton
        size="small"
        onClick={() => shift(-1)}
        aria-label="前日へ"
        sx={{ width: 22, height: 22, color: colorTokens.fg.muted }}
      >
        <ChevronLeftIcon sx={{ fontSize: ICON_SIZE.sm }} />
      </IconButton>
      <Tooltip title={isToday ? '' : '今日へ移動'} placement="top">
        <Box
          role={isToday ? undefined : 'button'}
          tabIndex={isToday ? undefined : 0}
          onClick={isToday ? undefined : jumpToToday}
          onKeyDown={
            isToday
              ? undefined
              : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    jumpToToday()
                  }
                }
          }
          sx={{
            cursor: isToday ? 'default' : 'pointer',
            fontSize: scaledFontSize('tag'),
            color: colorTokens.fg.default,
          }}
        >
          {formatDateLabel(date)}
        </Box>
      </Tooltip>
      <IconButton
        size="small"
        onClick={() => {
          if (!isToday) shift(1)
        }}
        disabled={isToday}
        aria-label="翌日へ"
        sx={{ width: 22, height: 22, color: colorTokens.fg.muted }}
      >
        <ChevronRightIcon sx={{ fontSize: ICON_SIZE.sm }} />
      </IconButton>
    </Stack>
  )
}
